import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    ChevronDown,
    ChevronUp,
    MapPin,
    NotebookText,
    Plus,
    X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/hooks";
import { addAddress } from "../../../../../Redux/customer-address/slice";
import { Address, CreateAddressRequest } from "../../../../../Redux/customer-address/types";
import {
    fetchAllCountries,
    fetchStatesByCountryName,
    fetchDistrictsByStateName,
} from "../../../../../Redux/location-mangment/slice";
import LocationPicker, { ResolvedAddress } from "../../../../globalcomponents/LocationPicker";
import { type SelectOption } from "../../CheckoutData";
import "./CheckoutAddressSection.css";

export type CheckoutAddressSectionProps = {
    addresses: Address[];
    selectedAddressId?: string | null;
    onSelectAddress: (addressId: string) => void;
    userName?: string | null;
    deliveryInstructions: string;
    onDeliveryInstructionsChange: (val: string) => void;
};

type CheckoutSelectProps = {
    label: string;
    placeholder: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    onSelect: (value: string) => void;
};

const CheckoutSelect = ({
    label,
    placeholder,
    value,
    options,
    disabled = false,
    loading = false,
    onSelect,
}: CheckoutSelectProps) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const selectedOption =
        options.find((option) => option.value?.toLowerCase() === value?.toLowerCase()) ||
        options.find((option) => option.label?.toLowerCase() === value?.toLowerCase()) ||
        null;

    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return options;
        const q = searchQuery.toLowerCase();
        return options.filter(
            (option) =>
                option.label.toLowerCase().includes(q) ||
                option.value.toLowerCase().includes(q)
        );
    }, [options, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
                setSearchQuery("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const showSearch = options.length > 5;

    return (
        <div className="checkout-form-field" ref={wrapperRef}>
            <label>{label}</label>

            <button
                type="button"
                className={`checkout-custom-select ${disabled ? "disabled" : ""}`}
                onClick={() => {
                    if (!disabled) {
                        setOpen((prev) => !prev);
                        setSearchQuery("");
                    }
                }}
                aria-expanded={open}
            >
                <span className={selectedOption ? "selected" : "checkout-select-placeholder"}>
                    {selectedOption ? (
                        <>
                            {selectedOption.flag && (
                                <span className="checkout-option-flag">
                                    {selectedOption.flag}
                                </span>
                            )}
                            {selectedOption.label}
                        </>
                    ) : (
                        placeholder
                    )}
                </span>

                {open ? (
                    <ChevronUp size={17} strokeWidth={1.9} />
                ) : (
                    <ChevronDown size={17} strokeWidth={1.9} />
                )}
            </button>

            {open && !disabled && (
                <div className="checkout-custom-select-menu">
                    {showSearch && (
                        <div className="checkout-select-search-container">
                            <input
                                type="text"
                                className="checkout-select-search-input"
                                placeholder={`Search ${label.toLowerCase()}...`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                    )}

                    {loading ? (
                        <div className="checkout-custom-select-empty">Loading options...</div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="checkout-custom-select-empty">No options found</div>
                    ) : (
                        filteredOptions.map((option) => (
                            <button
                                type="button"
                                key={option.value}
                                className={`checkout-custom-select-item ${
                                    selectedOption?.value === option.value ? "active" : ""
                                }`}
                                onClick={() => {
                                    onSelect(option.value);
                                    setOpen(false);
                                    setSearchQuery("");
                                }}
                            >
                                {option.flag && (
                                    <span className="checkout-option-flag">
                                        {option.flag}
                                    </span>
                                )}
                                <span>{option.label}</span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

const CheckoutAddressSection = ({
    addresses,
    selectedAddressId,
    onSelectAddress,
    userName = "Customer",
    deliveryInstructions,
    onDeliveryInstructionsChange,
}: CheckoutAddressSectionProps) => {
    const dispatch = useAppDispatch();
    const {
        countries,
        states,
        districts,
        loading: locLoading,
    } = useAppSelector((state) => state.location);

    const [instructionsOpen, setInstructionsOpen] = useState(false);
    const [newAddressOpen, setNewAddressOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia("(max-width: 860px)").matches : false
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mql = window.matchMedia("(max-width: 860px)");
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, []);

    const [formData, setFormData] = useState<CreateAddressRequest>({
        fullName: "",
        country: "India",
        state: "",
        district: "",
        city: "",
        email: "",
        phoneNumber: "",
        addressLine1: "",
        addressLine2: "",
        landmark: "",
        pincode: "",
        addressType: "Home",
        defaultAddress: false,
        latitude: undefined,
        longitude: undefined,
    });

    useEffect(() => {
        if (newAddressOpen) {
            if (countries.length === 0 && !locLoading) {
                dispatch(fetchAllCountries());
            }
            if (formData.country) {
                dispatch(fetchStatesByCountryName(formData.country));
            }
        }
    }, [newAddressOpen, countries.length, locLoading, formData.country, dispatch]);

    const countryOptions: SelectOption[] = useMemo(() => {
        if (countries && countries.length > 0) {
            return countries.map((c) => ({
                value: c.countryName,
                label: c.countryName,
                flag: c.countryFlag,
            })).sort((a, b) => a.label.localeCompare(b.label));
        }
        return [
            { value: "India", label: "India", flag: "🇮🇳" },
            { value: "United States", label: "United States", flag: "🇺🇸" },
            { value: "United Arab Emirates", label: "United Arab Emirates", flag: "🇦🇪" },
            { value: "Singapore", label: "Singapore", flag: "🇸🇬" },
        ];
    }, [countries]);

    const stateOptions: SelectOption[] = useMemo(() => {
        if (states && states.length > 0) {
            return states.map((s) => ({
                value: s.stateName,
                label: s.stateName,
            })).sort((a, b) => a.label.localeCompare(b.label));
        }
        return [];
    }, [states]);

    const districtOptions: SelectOption[] = useMemo(() => {
        if (districts && districts.length > 0) {
            return districts.map((d) => ({
                value: d.districtName,
                label: d.districtName,
            })).sort((a, b) => a.label.localeCompare(b.label));
        }
        return [];
    }, [districts]);

    const handleCountrySelect = useCallback((countryName: string) => {
        setFormData((prev) => ({
            ...prev,
            country: countryName,
            state: "",
            district: "",
        }));
        dispatch(fetchStatesByCountryName(countryName));
    }, [dispatch]);

    const handleStateSelect = useCallback((stateName: string) => {
        setFormData((prev) => ({
            ...prev,
            state: stateName,
            district: "",
        }));
        dispatch(fetchDistrictsByStateName(stateName));
    }, [dispatch]);

    const handleDistrictSelect = useCallback((districtName: string) => {
        setFormData((prev) => ({
            ...prev,
            district: districtName,
        }));
    }, []);

    const handleLocationChange = useCallback((lat: number, lng: number) => {
        setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    }, []);

    const handleAddressResolve = useCallback((addr: ResolvedAddress) => {
        setFormData((prev) => ({
            ...prev,
            country: addr.country || prev.country,
            city: addr.city || prev.city,
            state: addr.state || prev.state,
            district: addr.district || prev.district,
            pincode: addr.pincode || prev.pincode,
            addressLine1: prev.addressLine1 || addr.addressLine || "",
        }));
        if (addr.country) {
            dispatch(fetchStatesByCountryName(addr.country));
        }
        if (addr.state) {
            dispatch(fetchDistrictsByStateName(addr.state));
        }
    }, [dispatch]);

    useEffect(() => {
        if (!newAddressOpen || typeof window === "undefined") {
            return;
        }

        const isMobileScreen = window.matchMedia("(max-width: 860px)").matches;
        if (!isMobileScreen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setNewAddressOpen(false);
            }
        };

        window.addEventListener("keydown", handleEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [newAddressOpen]);

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = event.target;
        let val: string | boolean =
            type === "checkbox"
                ? (event.target as HTMLInputElement).checked
                : value;

        if (typeof val === "string") {
            if (name === "fullName") {
                val = val
                    .replace(/[^a-zA-Z\s]/g, "")
                    .replace(/\s{2,}/g, " ")
                    .slice(0, 40);
            } else if (name === "email") {
                val = val.replace(/[^a-zA-Z0-9@.]/g, "").slice(0, 30);
                const atCount = (val.match(/@/g) || []).length;
                const dotCount = (val.match(/\./g) || []).length;
                if (atCount > 1 || dotCount > 1) return;
                const numericCount = val.replace(/\D/g, "").length;
                if (numericCount > 6) return;
            } else if (name === "city") {
                val = val.replace(/[^a-zA-Z\s]/g, "").replace(/\s{2,}/g, " ");
                const spaceCount = (val.match(/\s/g) || []).length;
                if (spaceCount > 2) return;
                val = val.slice(0, 25);
            } else if (name === "phoneNumber" || name === "mobile") {
                val = val.replace(/\D/g, "").slice(0, 10);
            } else if (name === "addressLine1" || name === "addressLine2") {
                val = val.replace(/[^a-zA-Z0-9\s\-\/\_]/g, "").slice(0, 100);
            } else if (name === "landmark") {
                val = val.replace(/[^a-zA-Z0-9\s]/g, "").replace(/^\s+/, "").replace(/\s{2,}/g, " ");
                let digitCount = 0;
                val = val.replace(/\d/g, (d) => digitCount++ < 2 ? d : "");
                val = val.slice(0, 30);
            } else if (name === "pincode") {
                val = val.replace(/\D/g, "").slice(0, 6);
            }
        }

        setFormData((prev) => ({
            ...prev,
            [name === "mobile" ? "phoneNumber" : name]: val,
        }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!formData.fullName.trim()) {
            toast.warn("Please enter your full name", { autoClose: 3000 });
            return;
        }

        if (!formData.phoneNumber || formData.phoneNumber.length !== 10) {
            toast.warn("Mobile number must be exactly 10 digits", { autoClose: 3000 });
            return;
        }

        if (formData.email) {
            const atCount = (formData.email.match(/@/g) || []).length;
            const dotCount = (formData.email.match(/\./g) || []).length;
            if (atCount !== 1 || dotCount !== 1) {
                toast.warn("Email must contain exactly one '@' and one '.' character.", { autoClose: 3000 });
                return;
            }
        }

        if (!formData.country) {
            toast.warn("Please select a country", { autoClose: 3000 });
            return;
        }

        if (!formData.state) {
            toast.warn("Please select a state", { autoClose: 3000 });
            return;
        }

        if (!formData.district) {
            toast.warn("Please select a district", { autoClose: 3000 });
            return;
        }

        if (!formData.city.trim()) {
            toast.warn("Please enter your city", { autoClose: 3000 });
            return;
        }

        if (!formData.addressLine1.trim()) {
            toast.warn("Please enter Address Line 1", { autoClose: 3000 });
            return;
        }

        if (!formData.pincode || formData.pincode.length !== 6) {
            toast.warn("Pincode must be exactly 6 digits", { autoClose: 3000 });
            return;
        }

        if (formData.pincode === "000000") {
            toast.warn("Invalid pincode (000000)", { autoClose: 3000 });
            return;
        }

        const isDuplicate = addresses.some(
            (addr) =>
                addr.fullName?.trim().toLowerCase() === formData.fullName.trim().toLowerCase() &&
                addr.addressLine1?.trim().toLowerCase() === formData.addressLine1.trim().toLowerCase() &&
                addr.city?.trim().toLowerCase() === formData.city.trim().toLowerCase() &&
                addr.pincode?.trim() === formData.pincode.trim()
        );

        if (isDuplicate) {
            toast.warn("This address already exists.", { autoClose: 3000 });
            return;
        }

        if (formData.latitude == null || formData.longitude == null) {
            toast.warn("Please pin your exact delivery location on the map.", { autoClose: 3000 });
            return;
        }

        try {
            setIsSubmitting(true);
            const payload: CreateAddressRequest = {
                fullName: formData.fullName.trim(),
                phoneNumber: formData.phoneNumber.trim(),
                email: formData.email.trim(),
                country: formData.country,
                state: formData.state,
                district: formData.district,
                city: formData.city.trim(),
                addressLine1: formData.addressLine1.trim(),
                addressLine2: formData.addressLine2?.trim() || undefined,
                landmark: formData.landmark?.trim() || undefined,
                pincode: formData.pincode.trim(),
                addressType: formData.addressType || "Home",
                defaultAddress: formData.defaultAddress ?? false,
                latitude: formData.latitude,
                longitude: formData.longitude,
            };

            const res = await dispatch(addAddress(payload)).unwrap();
            toast.success("Address added successfully!", { autoClose: 3000 });
            setNewAddressOpen(false);

            const newId = (res as any)?.data?._id || (res as any)?._id;
            if (newId) {
                onSelectAddress(newId);
            }

            setFormData({
                fullName: "",
                country: "India",
                state: "",
                district: "",
                city: "",
                email: "",
                phoneNumber: "",
                addressLine1: "",
                addressLine2: "",
                landmark: "",
                pincode: "",
                addressType: "Home",
                defaultAddress: false,
                latitude: undefined,
                longitude: undefined,
            });
        } catch (err: any) {
            toast.error(err?.message || err || "Failed to add address", {
                autoClose: 3000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderAddressForm = (variant: "desktop" | "mobile") => (
        <form
            className={`checkout-address-form checkout-address-form-${variant}`}
            onSubmit={handleSubmit}
        >
            <div className="checkout-form-field checkout-form-field-full">
                <label>Full Name</label>
                <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter Full Name"
                />
            </div>

            <CheckoutSelect
                label="Select Country"
                placeholder="Select Country"
                value={formData.country}
                options={countryOptions}
                loading={locLoading && countries.length === 0}
                onSelect={handleCountrySelect}
            />

            <CheckoutSelect
                label="Select State"
                placeholder={formData.country ? "Select State" : "Select Country first"}
                value={formData.state}
                options={stateOptions}
                disabled={!formData.country}
                loading={locLoading && !!formData.country && states.length === 0}
                onSelect={handleStateSelect}
            />

            <CheckoutSelect
                label="Select District"
                placeholder={formData.state ? "Select District" : "Select State first"}
                value={formData.district}
                options={districtOptions}
                disabled={!formData.state}
                loading={locLoading && !!formData.state && districts.length === 0}
                onSelect={handleDistrictSelect}
            />

            <div className="checkout-form-field">
                <label>City</label>
                <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter City"
                />
            </div>

            <div className="checkout-form-field">
                <label>E-mail ID</label>
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter e-mail ID"
                />
            </div>

            <div className="checkout-form-field">
                <label>Mobile number</label>
                <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit mobile number"
                />
            </div>

            <div className="checkout-form-field">
                <label>Address Line - 1</label>
                <input
                    type="text"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    placeholder="Address Line 1"
                />
            </div>

            <div className="checkout-form-field">
                <label>Address Line - 2</label>
                <input
                    type="text"
                    name="addressLine2"
                    value={formData.addressLine2 || ""}
                    onChange={handleInputChange}
                    placeholder="Address Line 2 (optional)"
                />
            </div>

            <div className="checkout-form-field">
                <label>Landmark</label>
                <input
                    type="text"
                    name="landmark"
                    value={formData.landmark || ""}
                    onChange={handleInputChange}
                    placeholder="Landmark (optional)"
                />
            </div>

            <div className="checkout-form-field">
                <label>Pincode</label>
                <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="Enter 6-digit pincode"
                />
            </div>

            <div className="checkout-form-field checkout-form-field-full">
                <label>Address Type</label>
                <div className="checkout-address-type-group">
                    {["Home", "Office", "Other"].map((type) => (
                        <button
                            type="button"
                            key={type}
                            className={`checkout-address-type-btn ${
                                formData.addressType === type ? "active" : ""
                            }`}
                            onClick={() =>
                                setFormData((prev) => ({ ...prev, addressType: type }))
                            }
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            <div className="checkout-form-field checkout-form-field-full checkout-location-picker-field">
                <LocationPicker
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    onChange={handleLocationChange}
                    onAddressResolve={handleAddressResolve}
                    height={variant === "mobile" ? 200 : 230}
                />
            </div>

            <label className="checkout-save-info checkout-form-field-full">
                <input
                    type="checkbox"
                    name="defaultAddress"
                    checked={formData.defaultAddress || false}
                    onChange={handleInputChange}
                />
                <span>Save this information for next time</span>
            </label>

            <div className="checkout-form-actions checkout-form-field-full">
                <button
                    type="submit"
                    className="checkout-submit-button"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Saving address..." : "Save address"}
                </button>
            </div>
        </form>
    );

    return (
        <section className="checkout-left">
            <div className="checkout-section-card">
                <div className="checkout-address-header">
                    <div>
                        <span className="checkout-address-eyebrow">
                            DELIVERY ADDRESS
                        </span>

                        <h2>
                            Delivering to {userName || "Customer"}
                        </h2>
                    </div>

                    <button
                        type="button"
                        className="checkout-address-change"
                        onClick={() => setNewAddressOpen(true)}
                    >
                        + Add New
                    </button>
                </div>

                <div className="checkout-address-list">
                    {addresses.length === 0 ? (
                        <p style={{ padding: "16px", color: "#475467", fontSize: "13px" }}>
                            No saved addresses found. Please add a new delivery address below.
                        </p>
                    ) : (
                        addresses.map((address) => {
                            const addressId = address._id || "";
                            const isSelected = selectedAddressId === addressId;
                            const fullAddrText = [
                                address.addressLine1,
                                address.addressLine2,
                                address.landmark,
                                address.city,
                                address.district,
                                address.state,
                                address.pincode,
                                address.country,
                            ].filter(Boolean).join(", ");

                            return (
                                <label
                                    className={`checkout-address-item ${isSelected ? "active" : ""}`}
                                    key={addressId}
                                >
                                    <input
                                        type="radio"
                                        name="selectedAddress"
                                        checked={isSelected}
                                        onChange={() => addressId && onSelectAddress(addressId)}
                                    />

                                    <div className="checkout-address-content">
                                        <div className="checkout-address-topline">
                                            <span className="checkout-address-tag">
                                                {address.addressType || (address.defaultAddress ? "Default" : "Home")}
                                            </span>

                                            <strong>
                                                {address.fullName}
                                            </strong>

                                            <span>
                                                {address.phoneNumber}
                                            </span>
                                        </div>

                                        <p>
                                            {fullAddrText}
                                        </p>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>

                <button
                    type="button"
                    className="checkout-link-button"
                    onClick={() =>
                        setInstructionsOpen(
                            (prev) =>
                                !prev
                        )
                    }
                >
                    <NotebookText
                        size={16}
                        strokeWidth={2}
                    />

                    Add delivery instructions

                    {instructionsOpen ? (
                        <ChevronUp
                            size={16}
                            strokeWidth={1.8}
                        />
                    ) : (
                        <ChevronDown
                            size={16}
                            strokeWidth={1.8}
                        />
                    )}
                </button>

                {instructionsOpen && (
                    <div className="checkout-instructions-box">
                        <label>
                            Delivery instructions
                        </label>

                        <textarea
                            name="instructions"
                            value={deliveryInstructions}
                            onChange={(e) => onDeliveryInstructionsChange(e.target.value)}
                            placeholder="Add any helpful note for delivery partner. Example: Call before delivery, leave at front desk, landmark notes..."
                        />
                    </div>
                )}

                <div className="checkout-divider" />

                <button
                    type="button"
                    className="checkout-accordion-title"
                    onClick={() =>
                        setNewAddressOpen(
                            (prev) =>
                                !prev
                        )
                    }
                >
                    <span>
                        <Plus
                            size={17}
                            strokeWidth={2.1}
                        />

                        Add New address
                    </span>

                    {newAddressOpen ? (
                        <ChevronUp
                            size={18}
                            strokeWidth={1.9}
                        />
                    ) : (
                        <ChevronDown
                            size={18}
                            strokeWidth={1.9}
                        />
                    )}
                </button>

                {newAddressOpen && !isMobile && renderAddressForm("desktop")}
            </div>

            {newAddressOpen && isMobile && (
                <div
                    className="checkout-address-mobile-overlay"
                    role="presentation"
                >
                    <section
                        className="checkout-address-mobile-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Add new address"
                    >
                        <div className="checkout-address-mobile-sheet-head">
                            <div>
                                <span>
                                    NEW DELIVERY ADDRESS
                                </span>

                                <h2>
                                    Add new address
                                </h2>

                                <p>
                                    Enter your delivery details to continue checkout.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setNewAddressOpen(
                                        false
                                    )
                                }
                                aria-label="Close add address"
                            >
                                <X
                                    size={20}
                                    strokeWidth={2}
                                />
                            </button>
                        </div>

                        <div className="checkout-address-mobile-sheet-location">
                            <MapPin
                                size={15}
                                strokeWidth={2}
                            />

                            <span>
                                Delivery details
                            </span>
                        </div>

                        <div className="checkout-address-mobile-sheet-body">
                            {renderAddressForm(
                                "mobile"
                            )}
                        </div>
                    </section>
                </div>
            )}
        </section>
    );
};

export default CheckoutAddressSection;
